import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardStats from './components/dashboard/DashboardStats';
import BlockPlanning from './components/planning/BlockPlanning';
import WorkLogEntry from './components/operations/WorkLogEntry';
import CostAnalysis from './components/costing/CostAnalysis';
import EquipmentManagement from './components/production/EquipmentManager/EquipmentList';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ReportsPage from './pages/ReportsPage';
import PersonnelPage from './pages/PersonnelPage';
import SafetyPage from './pages/SafetyPage';
import SettingsPage from './pages/SettingsPage';

const PrivateRoute = ({ children, roles = [] }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return <div>Loading...</div>;
    }
    
    if (!user) {
        return <Navigate to="/login" />;
    }
    
    if (roles.length > 0 && !roles.includes(user.role)) {
        return <Navigate to="/dashboard" />;
    }
    
    return children;
};

const AppRoutes = () => {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            
            {/* All routes under DashboardLayout are protected */}
            <Route path="/" element={
                <PrivateRoute>
                    <DashboardLayout />
                </PrivateRoute>
            }>
                <Route index element={<Navigate to="/dashboard" />} />
                <Route path="dashboard" element={<DashboardStats />} />
                
                {/* Routes with additional role restrictions */}
                <Route path="planning" element={
                    <PrivateRoute roles={['admin', 'manager']}>
                        <BlockPlanning />
                    </PrivateRoute>
                } />
                <Route path="operations" element={
                    <PrivateRoute roles={['admin', 'supervisor', 'operator']}>
                        <WorkLogEntry />
                    </PrivateRoute>
                } />
                <Route path="costing" element={
                    <PrivateRoute roles={['admin', 'manager']}>
                        <CostAnalysis />
                    </PrivateRoute>
                } />
                <Route path="equipment" element={
                    <PrivateRoute roles={['admin', 'manager', 'maintenance', 'operator']}>
                        <EquipmentManagement />
                    </PrivateRoute>
                } />
                <Route path="cost-analysis" element={
                    <PrivateRoute roles={['admin', 'manager']}>
                        <CostAnalysis />
                    </PrivateRoute>
                } />
                <Route path="reports" element={
                    <PrivateRoute roles={['admin', 'manager', 'supervisor']}>
                        <ReportsPage />
                    </PrivateRoute>
                } />
                <Route path="personnel" element={
                    <PrivateRoute roles={['admin', 'manager']}>
                        <PersonnelPage />
                    </PrivateRoute>
                } />
                <Route path="safety" element={
                    <PrivateRoute roles={['admin', 'supervisor', 'operator']}>
                        <SafetyPage />
                    </PrivateRoute>
                } />
                <Route path="settings" element={
                    <PrivateRoute roles={['admin', 'manager']}>
                        <SettingsPage />
                    </PrivateRoute>
                } />
            </Route>
            
            {/* Catch-all route for 404 */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
    );
};

function App() {
    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#1890ff',
                    borderRadius: 6,
                },
            }}
        >
            <AuthProvider>
                <Router future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}>
                    <AppRoutes />
                </Router>
            </AuthProvider>
        </ConfigProvider>
    );
}

export default App;